/**
 * Lighting moods for the concourse.
 *
 * Real centres are not lit like an operating theatre. Reference photography of
 * daylit malls reads warm — 2700–3000 K downlights bouncing off stone, with the
 * skylight cooler but never the dominant note — and the eye reads that as
 * comfortable. A neutral-white interior at the same illuminance reads clinical
 * and, on a screen you're staring at, tiring.
 *
 * "Warm" is the default for that reason. "Daylight" keeps the cooler, brighter
 * rendering for anyone who prefers it, or for looking closely at a shopfront.
 *
 * A preset is applied by retinting existing materials rather than rebuilding the
 * scene, so switching is instant: every material's authored color is multiplied
 * by `tint`, and the lights, exposure and atmosphere are set from the rest.
 */
export interface AmbiencePreset {
  id: AmbienceId
  label: string
  /** Multiplied into every material color — the overall cast of the space. */
  tint: number
  exposure: number
  skyLight: number
  groundLight: number
  hemisphereIntensity: number
  sunColor: number
  sunIntensity: number
  ambientColor: number
  ambientIntensity: number
  /** Softens the neutral image-based bounce so it doesn't fight the tint. */
  environmentIntensity: number
  atmosphere: number
  fogDensity: number
}

export type AmbienceId = 'warm' | 'daylight'

export const AMBIENCE: Record<AmbienceId, AmbiencePreset> = {
  warm: {
    id: 'warm',
    label: 'Warm',
    tint: 0xffdcbc,
    exposure: 0.78,
    skyLight: 0xffe7d2,
    groundLight: 0xc4a882,
    hemisphereIntensity: 0.72,
    sunColor: 0xffd9a8,
    sunIntensity: 0.78,
    ambientColor: 0xffeedd,
    ambientIntensity: 0.14,
    environmentIntensity: 0.7,
    atmosphere: 0xe6d6c0,
    fogDensity: 0.008,
  },
  daylight: {
    id: 'daylight',
    label: 'Daylight',
    tint: 0xffffff,
    exposure: 0.92,
    skyLight: 0xdcecff,
    groundLight: 0xcdc4b3,
    hemisphereIntensity: 0.85,
    sunColor: 0xfff1de,
    sunIntensity: 0.85,
    ambientColor: 0xf4efe6,
    ambientIntensity: 0.12,
    environmentIntensity: 1.0,
    atmosphere: 0xd9e4ef,
    fogDensity: 0.0075,
  },
}

export const DEFAULT_AMBIENCE: AmbienceId = 'warm'

const STORAGE_KEY = 'mall3d:ambience'

export function loadAmbience(): AmbienceId {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'warm' || saved === 'daylight') return saved
  } catch { /* private mode / storage disabled */ }
  return DEFAULT_AMBIENCE
}

export function saveAmbience(id: AmbienceId) {
  try { localStorage.setItem(STORAGE_KEY, id) } catch { /* non-fatal */ }
}
